import moment from "moment";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChildren
} from "@angular/core";
import {INotice} from "../types";
import {Subscription} from "rxjs";
import {NoticeService, TaxpayerComment, User, PlainNotice, NoticeType} from "../../../../swaggerServices";
import {makeName} from "../../../shared/util/makeName";
import {NoticesStateService} from "../notices-state.service";
import {UserInfoService} from "../../user-info.service";
import {asMap} from "../../../shared/util/asMap";

const formatDate = d => d ? moment(d).format("MMMM Do YYYY, h:mm:ss a") : "";

interface ITaxpayerComment extends TaxpayerComment {
  postDate?: string;
  editDate?: string;
  user?: User;
  archivedDate?: string;

  editing?: boolean;
  editingMessage?: string;
  saving?: boolean;

  noticeInfo?: string;
}

const makeNoticeInfo = (notice?: PlainNotice, noticeType?: NoticeType): string => {
  return [
    notice && notice.year || "",
    notice && notice.period || "",
    noticeType && noticeType.name || "",
  ].filter(x => x).join(" ");
};

@Component({
    selector: 'app-notice-comments',
    templateUrl: './notice-comments.component.html',
    styleUrls: ['./notice-comments.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NoticeCommentsComponent implements OnInit, OnChanges {
  @Input() notice?: INotice;
  @Input() taxpayerAlias: string;
  comments: ITaxpayerComment[] = [];
  visibleComments: ITaxpayerComment[] = [];
  showArchived: boolean = false;
  commentText: string = "";
  loadingComments: boolean = false;
  saving: boolean = false;
  fetchCommentsSub?: Subscription;

  @ViewChildren("commentEditBox") commentEditBoxes: QueryList<ElementRef>;

  constructor(
    private cdr: ChangeDetectorRef,
    private noticeService: NoticeService,
    private noticesState: NoticesStateService,
    public userInfo: UserInfoService) { }

  ngOnInit() {
    this.fetchComments();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.notice) {
      this.fetchComments();
    }
  }

  fetchComments() {
    if (!this.notice || !this.notice.atlasId) {
      this.loadingComments = false;
      this.comments = [];
      this.filterVisibleComments();
      this.cdr.detectChanges();
      return;
    }
    if (this.fetchCommentsSub) {
      this.fetchCommentsSub.unsubscribe();
    }
    this.loadingComments = true;
    this.fetchCommentsSub = this.noticeService.getCommentForTaxpayer(this.notice.atlasId).subscribe(({ comments, notices, noticeTypes }) => {
      const noticesById = asMap<number, PlainNotice>(notices, n => n.id);
      const noticeTypesById = asMap<number, NoticeType>(noticeTypes, nt => nt.id);

      this.loadingComments = false;
      this.comments = comments.map((c: TaxpayerComment) => {
        const n = noticesById.get(c.noticeId);
        const nt = n ? noticeTypesById.get(n.noticeTypeId) : null;
        return {
          ...c,
          postDate: formatDate(c.createdAt),
          editDate: formatDate(c.messageEditedAt),
          archivedDate: formatDate(c.archivedAt),
          user: this.noticesState.resolveUser(c.userId),
          noticeInfo: makeNoticeInfo(n, nt),
        };
      });
      this.filterVisibleComments();
      this.cdr.detectChanges();
    }, e => {
      this.loadingComments = false;
      this.cdr.detectChanges();
    });
  }

  filterVisibleComments() {
    this.visibleComments = this.showArchived ? this.comments : this.comments.filter(c => !c.archivedAt);
  }

  postComment() {
    this.saving = true;
    this.noticeService.postTaxpayerComment(this.notice.atlasId, { noticeId: this.notice.id, message: this.commentText || "" }).subscribe(() => {
      this.saving = false;
      this.commentText = "";
      this.fetchComments();
      this.cdr.detectChanges();
    }, e => {
      this.saving = false;
      this.cdr.detectChanges();
    });
  }

  commentTrackBy(index: number, comment: ITaxpayerComment) {
    return comment.id;
  }

  makeName(user) {
    return makeName(user);
  }

  toggleShowArchived() {
    this.showArchived = !this.showArchived;
    this.filterVisibleComments();
  }

  startEditing(comment: ITaxpayerComment, index: number) {
    comment.editing = true;
    comment.editingMessage = comment.message;
    const el = this.commentEditBoxes.find((_ , i) => i === index);
    if (el) {
      setTimeout(() => {
        el.nativeElement.focus();
      }, 0);
    }
  }

  cancelEditing(comment: ITaxpayerComment) {
    comment.editing = false;
    comment.editingMessage = "";
  }

  archiveComment(comment: ITaxpayerComment, value: boolean) {
    comment.saving = true;
    this.noticeService.updateTaxpayerComment(comment.id, {
      archive: value,
    }).subscribe(() => {
      comment.saving = false;
      this.fetchComments();
      this.cdr.detectChanges();
    }, e => {
      comment.saving = false;
      this.cdr.detectChanges();
    });
  }

  editComment(comment: ITaxpayerComment) {
    comment.saving = true;
    this.noticeService.updateTaxpayerComment(comment.id, {
      message: comment.editingMessage,
    }).subscribe(() => {
      comment.saving = false;
      this.fetchComments();
      this.cdr.detectChanges();
    }, e => {
      comment.saving = false;
      this.cdr.detectChanges();
    });
  }
}
